<template>
  <v-card :class="actionClass">
    <!-- Section meta info & toolbar-->
    <v-toolbar flat>
      <v-toolbar-title class="blue-grey--text">
        <span class="subtitle-1">{{ isRootSection ? 'Root section' : 'Section' }}</span>
        <span class="caption pl-2">LVL {{ level }}</span>
        <span class="caption pl-2">IDX {{ index }}</span>
      </v-toolbar-title>
      <v-spacer :class="index >  0 ? 'handle' : ''"></v-spacer>
      <!-- Context menu for root section -->
      <v-menu open-on-hover offset-y v-if="isRootSection && toolbarEnabled && displayPaste">
        <template v-slot:activator="{ on }">
          <v-btn text icon class="caption" v-on="on">
            <v-icon>more_vert</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-btn text icon :title="$t('paste')" @click="paste">
            <v-icon>note_add</v-icon>
          </v-btn>
        </v-list>
      </v-menu>
      <!-- Context menu for embeded sections -->
      <v-menu open-on-hover left offset-x v-if="displayActions && toolbarEnabled">
        <template v-slot:activator="{ on }">
          <v-btn text icon class="caption" v-on="on">
            <v-icon>more_vert</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-btn text icon :title="$t('paste')" @click="paste" v-if="displayPaste">
            <v-icon>note_add</v-icon>
          </v-btn>
          <v-btn text icon :title="copyTitle" :color="inCopy ? 'grey' : 'black'" @click="copy">
            <v-icon>file_copy</v-icon>
          </v-btn>
          <v-btn text icon :title="cutTitle" :color="inCut ? 'grey' : 'black'" @click="cut">
            <v-icon>flip_to_back</v-icon>
          </v-btn>
          <v-btn text icon color="error" :title="$t('remove')" @click="remove">
            <v-icon>delete_forever</v-icon>
          </v-btn>
        </v-list>
      </v-menu>
    </v-toolbar>

    <!-- Section fields editor -->
    <v-container fluid v-if="! isRootSection">
      <v-layout>
        <v-flex>
          <v-text-field v-model="localLabel" :label="$t('label')" data-test="checklist-info-label"></v-text-field>
        </v-flex>
        <v-flex v-if="selectedLanguage" class="ml-3">
          <v-text-field v-model="localTranslatedLabel" :label="translatedLabel"></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex>
          <v-text-field
            v-model="localDescription"
            :label="$t('description')"
            data-test="checklist-info-description"
          ></v-text-field>
        </v-flex>
        <v-flex v-if="selectedLanguage" class="ml-3">
          <v-text-field v-model="localTranslatedDescription" :label="translatedDescription"></v-text-field>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- Section items -->
    <draggable v-model="sortedItems" handle=".handle" ghostClass="ghost" chosenClass="drag">
      <div v-for="(item, key) in items" :key="key" class="ml-2 mt-2 sortable">
        <section-editor
          v-if="(/section/).test(item.type)"
          :eventBus="eventBus"
          :type="item.type"
          :level="item.lvl"
          :index="item.idx"
          :items="item.items"
          :label="item.label"
          :description="item.description"
          :translation="item.translation || {}"
        ></section-editor>
        <info-editor
          v-if="(/info/).test(item.type)"
          :eventBus="eventBus"
          :type="item.type"
          :level="item.lvl"
          :index="item.idx"
          :label="item.label"
          :description="item.description"
          :translation="item.translation || {}"
        ></info-editor>
        <image-editor
          v-if="(/image/).test(item.type)"
          :eventBus="eventBus"
          :type="item.type"
          :level="item.lvl"
          :index="item.idx"
          :items="item.items"
          :label="item.label"
          :description="item.description"
          :translation="item.translation || {}"
        ></image-editor>
        <input-editor
          v-if="(/switch|number|string|file/).test(item.type)"
          :eventBus="eventBus"
          :type="item.type"
          :level="item.lvl"
          :index="item.idx"
          :label="item.label"
          :description="item.description"
          :translation="item.translation || {}"
          :validations="item.validations"
          :items="item.items"
          :source="item.source || null"
          :length="item.length"
        ></input-editor>
        <graph-editor
          v-if="(/graph/).test(item.type)"
          :eventBus="eventBus"
          :type="item.type"
          :level="item.lvl"
          :index="item.idx"
          :label="item.label"
          :description="item.description"
          :translation="item.translation || {}"
          :source="item.source || null"
        ></graph-editor>
      </div>
    </draggable>

    <!-- Section content editor -->
    <v-toolbar flat class="mt-2">
      <v-spacer></v-spacer>
      <v-menu open-on-hover offset-y v-if="toolbarEnabled">
        <template v-slot:activator="{ on }">
          <v-btn text class="caption" v-on="on" data-test="new-checklist-info">
            <v-icon>add</v-icon>
            {{ $t('info') }}
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="addInfo" data-test="checklist-info-text">
            <v-list-item-avatar>
              <v-icon>add</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ $t('text') }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t('block-of-text') }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="addImage" data-test="checklist-info-image">
            <v-list-item-avatar>
              <v-icon>add</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ $t('image') }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t('visual-block') }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-menu open-on-hover offset-y v-if="toolbarEnabled">
        <template v-slot:activator="{ on }">
          <v-btn text class="caption" v-on="on">
            <v-icon>add</v-icon>
            {{ $t('input') }}
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="addSwitchInput">
            <v-list-item-avatar>
              <v-icon>add</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ $t('switch') }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t('boolean-selector') }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="addNumberInput">
            <v-list-item-avatar>
              <v-icon>add</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ $t('number') }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t('number-input') }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="addStringInput">
            <v-list-item-avatar>
              <v-icon>add</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ $t('text') }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t('text-input') }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="addFileInput">
            <v-list-item-avatar>
              <v-icon>add</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ $t('image') }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t('file-input') }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="addGraphInput">
            <v-list-item-avatar>
              <v-icon>add</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ $t('graph') }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t('graph-input') }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn
        text
        class="caption"
        @click="addSection"
        v-if="toolbarEnabled"
        data-test="new-checklist-section"
      >
        <v-icon>add</v-icon>
        {{ $t('section') }}
      </v-btn>
    </v-toolbar>
  </v-card>
</template>

<style>
.handle {
  cursor: move;
  height: 25px;
}

.ghost {
  opacity: 0.5;
  border: 1px dashed rgb(88, 137, 228) !important;
}

.drag {
  border: 1px solid rgb(88, 137, 228) !important;
  border-radius: 2px;
}
</style>

<script>
import Draggable from 'vuedraggable'
import editorMixins from './editor-mixins.es6'
import SectionEditor from './editor-section.vue'
import InputEditor from './editor-input.vue'
import InfoEditor from './editor-info.vue'
import ImageEditor from './editor-image.vue'
import GraphEditor from './editor-graph.vue'

export default {
  name: 'section-editor',
  mixins: [editorMixins],
  props: {
    level: Number,
    index: Number,
    items: Array,
    label: String,
    description: String,
    translation: Object
  },
  methods: {
    // Append new item: info
    addInfo () {
      this.eventBus.$emit('insert', {
        idx: this.index,
        item: this.createInfo()
      })
    },

    // Append new item: image
    addImage () {
      this.eventBus.$emit('insert', {
        idx: this.index,
        item: this.createImage()
      })
    },

    // Append new item: section
    addSection () {
      this.eventBus.$emit('insert', {
        idx: this.index,
        item: this.createSection()
      })
    },

    addSwitchInput () {
      this.eventBus.$emit('insert', {
        idx: this.index,
        item: this.createInput('switch')
      })
    },

    addNumberInput () {
      this.eventBus.$emit('insert', {
        idx: this.index,
        item: this.createInput('number')
      })
    },

    addStringInput () {
      this.eventBus.$emit('insert', {
        idx: this.index,
        item: this.createInput('string')
      })
    },

    addFileInput () {
      this.eventBus.$emit('insert', {
        idx: this.index,
        item: this.createInput('file')
      })
    },

    addGraphInput () {
      this.eventBus.$emit('insert', {
        idx: this.index,
        item: this.createInput('graph')
      })
    },

    createInfo () {
      return {
        idx: this.nextIdx,
        lvl: this.nextLevel,
        type: 'info',
        label: '',
        description: '',
        translation: {}
      }
    },

    createImage () {
      return {
        idx: this.nextIdx,
        lvl: this.nextLevel,
        type: 'image',
        label: '',
        description: '',
        items: []
      }
    },

    createSection () {
      return {
        idx: this.nextIdx,
        lvl: this.nextLevel,
        type: 'section',
        label: '',
        description: '',
        translation: {},
        items: []
      }
    },

    createInput (type) {
      let input = {
        idx: this.nextIdx,
        lvl: this.nextLevel,
        type,
        label: '',
        description: '',
        translation: {},
        validations: [],
        items: [],
        source: null
      }
      if (type === 'graph') {
        input.source = {
          name: '',
          type: '',
          unit: null,
          displayUnit: null
        }
      }
      return input
    }
  },
  components: {
    Draggable,
    SectionEditor,
    InputEditor,
    InfoEditor,
    ImageEditor,
    GraphEditor
  }
}
</script>
